import { useQuery, requestDeals } from '@workspace/api';

export interface QuoteStats {
    in_progress_today: number;
    an_won_today: number;
    av_won_today: number;
    orange_today: number;
}

export interface DashboardStats {
    today: QuoteStats;
    month: {
        total: number;
        won: number;
        in_progress: number;
        cancelled: number;
        conversion_rate: number;
    };
    top_products: Array<{
        code_produit: string;
        count: number;
    }>;
    top_apporteurs: Array<{
        nom_apporteur_1: string;
        count: number;
    }>;
    weekly_evolution: Array<{
        date: string;
        total: number;
        won: number;
        in_progress: number;
    }>;
    promo: {
        with_promo: number;
        total: number;
        total_reduction: number;
        usage_rate: number;
    };
}

/**
 * Hook pour récupérer les statistiques journalières des devis
 */
export function useQuoteStats() {
    return useQuery<QuoteStats>({
        queryKey: ['deals', 'stats', 'quotes'],
        queryFn: async () => {
            const response = await requestDeals.get<QuoteStats>('/api/stats/quotes');
            return response.data;
        },
        staleTime: 30 * 1000, // 30 secondes
        refetchInterval: 60 * 1000, // Refetch toutes les 60 secondes
    });
}

/**
 * Hook pour récupérer toutes les statistiques du dashboard
 */
export function useDashboardStats() {
    return useQuery<DashboardStats>({
        queryKey: ['deals', 'stats', 'dashboard'],
        queryFn: async () => {
            const response = await requestDeals.get<DashboardStats>('/api/stats/dashboard');
            return response.data;
        },
        staleTime: 60 * 1000, // 1 minute
        refetchInterval: 5 * 60 * 1000, // Refetch toutes les 5 minutes
    });
}

interface RealisateurEntry {
    nom_realisateur: string;
    id_realisateur: number;
    count: number;
}

export interface AFNByRealisateurStats {
    period: string;
    devis: {
        global: number;
        by_realisateur: RealisateurEntry[];
    };
    afn: {
        global: number;
        by_realisateur: RealisateurEntry[];
    };
}

export interface AFNByProductStats {
    period: string;
    code_produit: string | null;
    total: number;
    daily_evolution: Array<{
        date: string;
        count: number;
    }> | null;
}

export interface PromoVolumeStats {
    code_promo: string;
    total: number;
    daily_usage: Array<{
        date: string;
        count: number;
    }>;
}

export interface AFNByApporteurStats {
    apporteur: string;
    period: string;
    count: number;
}

export interface AFNByProductsBatchStats {
    period: string;
    total: number;
    by_product: Array<{
        code_produit: string;
        count: number;
    }>;
    daily_evolution: Array<{
        date: string;
        count: number;
    }> | null;
}

export interface AFNByApporteursBatchStats {
    period: string;
    apporteurs: Array<{
        name: string;
        count: number;
    }>;
}

export interface TaskStats {
    period: string;
    total: number;
    by_apporteur: Array<{
        nom_apporteur: string;
        count: number;
    }>;
    daily_evolution: {
        april_moto: Array<{ date: string; count: number }>;
        les_furets: Array<{ date: string; count: number }>;
        le_lynx: Array<{ date: string; count: number }>;
        lsa: Array<{ date: string; count: number }>;
    } | null;
}

export interface PMRealtimeStats {
    global: {
        total_contracts: number;
        total_prime: number;
        avg_prime: number;
    };
    by_realisateur: Array<{
        nom_realisateur: string;
        id_realisateur: number;
        total_contracts: number;
        total_prime: number;
        avg_prime: number;
    }>;
}

interface TypeMerPeriodStats {
    in_progress: number;
    won: number;
}

export interface TypeMerStats {
    fiche: {
        today: TypeMerPeriodStats;
        week: TypeMerPeriodStats;
        month: TypeMerPeriodStats;
    };
    app_gpt: {
        today: TypeMerPeriodStats;
        week: TypeMerPeriodStats;
        month: TypeMerPeriodStats;
    };
    agent_conv: {
        today: TypeMerPeriodStats;
        week: TypeMerPeriodStats;
        month: TypeMerPeriodStats;
    };
    // optionnel : absent tant que le back DEALS exposant la cle n'est pas deploye / cache expire
    autosave?: {
        today: TypeMerPeriodStats;
        week: TypeMerPeriodStats;
        month: TypeMerPeriodStats;
    };
}

export function useTypeMerStats() {
    return useQuery<TypeMerStats>({
        queryKey: ['deals', 'stats', 'type-mer'],
        queryFn: async () => {
            const response = await requestDeals.get<TypeMerStats>('/api/stats/type-mer');
            return response.data;
        },
        staleTime: 60 * 1000,
        refetchInterval: 5 * 60 * 1000,
    });
}

export function usePMRealtime() {
    return useQuery<PMRealtimeStats>({
        queryKey: ['deals', 'stats', 'pm-realtime'],
        queryFn: async () => {
            const response = await requestDeals.get<PMRealtimeStats>('/api/stats/pm/realtime');
            return response.data;
        },
    });
}

export interface PMRealisateurQuote {
    id: number;
    id_devis: number;
    code_produit: string;
    prime_totale_annuelle_frac_choisi: number | null;
    created_at: string;
    updated_at: string;
}

export function usePMRealisateurQuotes(idRealisateur: number, enabled: boolean = true) {
    return useQuery<{ quotes: PMRealisateurQuote[] }>({
        queryKey: ['deals', 'stats', 'pm-realisateur', idRealisateur],
        queryFn: async () => {
            const response = await requestDeals.get<{ quotes: PMRealisateurQuote[] }>(`/api/stats/pm/realisateur/${idRealisateur}`);
            return response.data;
        },
        enabled,
    });
}

/**
 * Hook pour les stats AFN par réalisateur
 */
export function useAFNByRealisateur(period: 'today' | 'yesterday' | 'week' | 'month' = 'today') {
    return useQuery<AFNByRealisateurStats>({
        queryKey: ['deals', 'stats', 'afn-realisateur', period],
        queryFn: async () => {
            const response = await requestDeals.get<AFNByRealisateurStats>('/api/stats/afn/realisateur', {
                params: { period },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
    });
}

/**
 * Hook pour les stats AFN par produit
 */
export function useAFNByProduct(period: 'today' | 'yesterday' | 'week' | 'month' = 'today', codeProduit?: string) {
    return useQuery<AFNByProductStats>({
        queryKey: ['deals', 'stats', 'afn-product', period, codeProduit],
        queryFn: async () => {
            const response = await requestDeals.get<AFNByProductStats>('/api/stats/afn/product', {
                params: { period, code_produit: codeProduit },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
    });
}

/**
 * Hook pour le volume d'un code promo
 */
export function usePromoVolume(codePromo: string, enabled: boolean = true) {
    return useQuery<PromoVolumeStats>({
        queryKey: ['deals', 'stats', 'promo-volume', codePromo],
        queryFn: async () => {
            const response = await requestDeals.get<PromoVolumeStats>('/api/stats/promo/volume', {
                params: { code_promo: codePromo },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
        enabled: enabled && !!codePromo,
    });
}

/**
 * Hook pour les stats AFN par apporteur
 */
export function useAFNByApporteur(apporteur: string, period: 'today' | 'yesterday' | 'week' | 'month' = 'today', enabled: boolean = true) {
    return useQuery<AFNByApporteurStats>({
        queryKey: ['deals', 'stats', 'afn-apporteur', apporteur, period],
        queryFn: async () => {
            const response = await requestDeals.get<AFNByApporteurStats>('/api/stats/afn/apporteur', {
                params: { apporteur, period },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
        enabled: enabled && !!apporteur,
    });
}

/**
 * Hook batch : tous les produits en 1 appel (remplace 7x useAFNByProduct)
 */
export function useAFNByProducts(period: 'today' | 'yesterday' | 'week' | 'month' = 'today') {
    return useQuery<AFNByProductsBatchStats>({
        queryKey: ['deals', 'stats', 'afn-products', period],
        queryFn: async () => {
            const response = await requestDeals.get<AFNByProductsBatchStats>('/api/stats/afn/products', {
                params: { period },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
    });
}

/**
 * Hook batch : tous les apporteurs en 1 appel (remplace 9x useAFNByApporteur)
 */
export function useAFNByApporteurs(period: 'today' | 'yesterday' | 'week' | 'month' = 'today') {
    return useQuery<AFNByApporteursBatchStats>({
        queryKey: ['deals', 'stats', 'afn-apporteurs', period],
        queryFn: async () => {
            const response = await requestDeals.get<AFNByApporteursBatchStats>('/api/stats/afn/apporteurs', {
                params: { period },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
    });
}

export type TeleventePeriod = 'today' | 'yesterday' | '7d' | '30d' | 'custom';

/**
 * Volumes/rates communs aux scopes AS et Global (mêmes clés, périmètre de
 * ventes différent : AS = ventes attribuées à un appel sortant / non liées à
 * un appel entrant, Global = toutes les ventes ADM confondues).
 */
export interface TeleventeVolumes {
    leads: number;
    joignables: number;
    argumented: number;
    sales: number;
    outbound_sales: number;
    pcc3: number;
    f4: number;
    f3: number;
    f3_eligible_sales: number;
    total_prime: number;
    avg_prime: number;
}

export interface TeleventeRates {
    joignability: number;
    argumented: number;
    transfo_leads: number;
    transfo_argumented: number;
    pcc3_on_sales: number;
    f4_on_sales: number;
    f3_on_eligible_sales: number;
    /** Part des ventes portant un appel sortant statué vente. Scope AS uniquement. */
    tracked_sales_share?: number;
}

/**
 * "Appels pris" est un proxy basé sur appel_status (cf.
 * config('televente.ae_missed_statuses') côté deals) : call_notifications ne
 * trace aucun appel entrant abandonné avant Hermes, donc pas de vrai signal
 * PBX/ACD de décroché disponible.
 */
export interface TeleventeAeVolumes {
    appels_recus: number;
    appels_pris: number;
    ventes: number;
    polluants: number;
    pcc3: number;
    f4: number;
    avg_prime: number;
    total_prime: number;
}

export interface TeleventeAeRates {
    qs: number;
    transfo_appels_recus: number;
    /** Pendant AE de "transfo/contacts arg." (AS) : ventes / appels pris. */
    transfo_appels_pris: number;
    taux_polluants: number;
    pcc3_on_sales: number;
    f4_on_sales: number;
}

export interface TeleventeFunnelStep {
    label: string;
    count: number;
    rate: number;
}

export interface TeleventeAsBlock {
    volumes: TeleventeVolumes;
    rates: TeleventeRates;
    funnel: TeleventeFunnelStep[];
    non_sale_statuses: Array<{ status: number; label: string; count: number }>;
    formules: Array<{ formule: string; count: number; rate: number }>;
    previous: { volumes: TeleventeVolumes; rates: TeleventeRates };
}

export interface TeleventeAeBlock {
    volumes: TeleventeAeVolumes;
    rates: TeleventeAeRates;
    previous: { volumes: TeleventeAeVolumes; rates: TeleventeAeRates };
}

export interface TeleventeGlobalBlock {
    volumes: TeleventeVolumes;
    rates: TeleventeRates;
    funnel: TeleventeFunnelStep[];
    formules: Array<{ formule: string; count: number; rate: number }>;
    by_product: Array<{ code_produit: string; count: number; rate: number }>;
    by_apporteur: Array<{ name: string; count: number; rate: number }>;
    previous: { volumes: TeleventeVolumes; rates: TeleventeRates };
}

export interface TeleventeAdvisorRow {
    advisor_code: string;
    hermes_id: string;
    as: {
        leads: number;
        joignables: number;
        argumented: number;
        outbound_sales: number;
        /** Ventes AS du conseiller : total moins la part AE. */
        sales: number;
        pcc3: number;
        f4: number;
        avg_prime: number;
        rates: {
            joignability: number;
            argumented: number;
            transfo_leads: number;
            transfo_argumented: number;
            pcc3_on_sales: number;
            f4_on_sales: number;
        };
    };
    ae: {
        appels_recus: number;
        appels_pris: number;
        ventes: number;
        polluants: number;
        pcc3: number;
        f4: number;
        avg_prime: number;
        rates: {
            qs: number;
            transfo_appels_recus: number;
            transfo_appels_pris: number;
            taux_polluants: number;
            pcc3_on_sales: number;
            f4_on_sales: number;
        };
    };
    common: {
        sales: number;
        pcc3: number;
        f4: number;
        avg_prime: number;
        rates: {
            pcc3_on_sales: number;
            f4_on_sales: number;
        };
    };
}

export interface TeleventeDailyPoint {
    date: string;
    leads: number;
    joignables: number;
    sales: number;
    joignability_rate: number;
}

export interface TeleventeDashboardStats {
    period: {
        label: string;
        from: string;
        to: string;
    };
    as: TeleventeAsBlock;
    ae: TeleventeAeBlock;
    global: TeleventeGlobalBlock;
    by_advisor: TeleventeAdvisorRow[];
    daily_evolution: TeleventeDailyPoint[];
}

/**
 * Hook pour le dashboard Manager Télévente (KPIs globaux, funnel, tableau conseiller).
 */
export function useTeleventeDashboard(period: TeleventePeriod = 'today', range?: { from?: string; to?: string }) {
    return useQuery<TeleventeDashboardStats>({
        queryKey: ['deals', 'stats', 'televente-dashboard', period, range?.from, range?.to],
        queryFn: async () => {
            const response = await requestDeals.get<TeleventeDashboardStats>('/api/stats/dashboard/televente', {
                params: { period, from: range?.from, to: range?.to },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
        refetchInterval: 2 * 60 * 1000,
    });
}

/**
 * Hook pour les stats tâches
 */
export function useTaskStats(period: 'today' | 'yesterday' | 'week' | 'month' = 'today') {
    return useQuery<TaskStats>({
        queryKey: ['deals', 'stats', 'tasks', period],
        queryFn: async () => {
            const response = await requestDeals.get<TaskStats>('/api/stats/tasks', {
                params: { period },
            });
            return response.data;
        },
        staleTime: 60 * 1000,
    });
}
